<template>
  <main>
    <div class="b-dialog__top-panel">
      <h2>{{ $t('CONTENT.counterparty.vatSettings') }}</h2>
      <button
        class="b-btn b-btn_border_none"
        @click.prevent="$emit('close')"
      >
        <dynamic-icon icon="close" />
      </button>
    </div>

    <form
      class="b-form b-dialog__form"
      @submit.prevent="save"
    >
      <div class="b-settings__block-box row row-no-flex mt-4">
        <div class="row mx-0 mb-2 b-form__container_type_flex b-settings__header">
          <div class="col-3 pr-4">
            <label class="b-settings__label">Ставка, %</label>
            <span class="b-form__required">*</span>
          </div>

          <div class="col-4 pr-4">
            <label class="b-settings__label">Дата начала</label>
            <span class="b-form__required">*</span>
          </div>

          <div class="col"></div>
        </div>

        <!-- 👇 РЕНДЕР ТОЛЬКО ОТСОРТИРОВАННЫХ ДАННЫХ -->
        <div
          v-for="(rate, index) in sortedRates"
          :key="rate.idInDB || index"
          class="b-settings__block-item"
        >
          <div class="row mx-0 mb-5 b-form__container_type_flex">
            <div class="col-3 pr-4">
              <div class="b-form__container">
                <cool-select
                  :list="vatRateOptions"
                  option-value="value"
                  option-text="label"
                  :placeholder="$t('CONTENT.choose')"
                  v-model="rate.value"
                  :isDisabled="rate.isReadonly"
                />
              </div>
            </div>

            <div class="col-4 pr-4">
              <div class="b-form__container">
                <datepicker
                  class="b-datepicker"
                  v-model="rate.dateFrom"
                  :disabled="rate.isReadonly"
                />
              </div>
            </div>

            <div class="b-form__container_type_flex b-form__container_type_flex_end">
              <button
                type="button"
                class="b-btn b-btn_border_none"
                @click.prevent="removeRate(rate)"
              >
                <dynamic-icon
                  class="b-btn__icon"
                  icon="close"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="row mx-0 mt-4">
          <div class="b-form__container">
            <button
              type="button"
              class="b-btn"
              @click.prevent="addRate"
            >
              <dynamic-icon
                class="b-btn__icon b-btn__icon_color_main-text"
                icon="plus"
              />
              Добавить ставку
            </button>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-6 offset-6">
          <div class="b-form__container b-form__container_type_flex b-form__container_type_flex_end">
            <button
              class="b-btn b-btn_type_primary"
              type="submit"
              :disabled="isSaveDisabled"
            >
              Сохранить
            </button>

            <button
              class="b-btn b-btn_type_empty m-2"
              type="button"
              @click.prevent="$emit('close')"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
export default {
  name: 'VatSettingsForm',

  props: {
    rates: {
      type: Array,
      default: () => []
    },
    counterpartyTypeId: {
      type: Number,
      required: false
    },
    isCreate: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      localRates: [],
      vatRateOptions: [
        { value: 0, label: '0%' },
        { value: 5, label: '5%' },
        { value: 7, label: '7%' },
        { value: 20, label: '20%' },
        { value: 22, label: '22%' }
      ]
    };
  },

  computed: {
    /** 👇 ВСЕГДА ОТСОРТИРОВАНО ПО ДАТЕ */
    sortedRates() {
      return [...this.localRates].sort((a, b) => {
        if (!a.dateFrom && !b.dateFrom) return 0;
        if (!a.dateFrom) return 1;
        if (!b.dateFrom) return -1;

        return new Date(a.dateFrom) - new Date(b.dateFrom);
      });
    },

    isSaveDisabled() {
      const hasEmptyFields = this.localRates.some(rate =>
        rate.value === null ||
        rate.value === undefined ||
        rate.dateFrom === null ||
        rate.dateFrom === undefined
      );

      if (hasEmptyFields) {
        return true;
      }

      const dates = this.localRates.map(rate => {
        const d = rate.dateFrom;

        if (!(d instanceof Date)) {
          return String(d);
        }

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
      });

      const uniqueDates = new Set(dates);
      return uniqueDates.size !== dates.length;
    }
  },

  watch: {
    rates: {
      immediate: true,
      deep: true,
      handler(newRates) {
        if (this.isCreate) {
          this.localRates = [];
          return;
        }

        if (Array.isArray(newRates) && newRates.length) {
          this.localRates = newRates.map(r => ({
            idInDB: r.idInDB || null,
            value: r.rateVAT,
            dateFrom: r.dateOfChange,
            isReadonly: false
          }));
        } else {
          this.localRates = [];
        }
      }
    }
  },

  methods: {
    addRate() {
      this.localRates.push({
        idInDB: null,
        value: null,
        dateFrom: null,
        isReadonly: false
      });
    },

    removeRate(rate) {
      const index = this.localRates.indexOf(rate);
      if (index !== -1) {
        this.localRates.splice(index, 1);
      }
    },

    save() {
      if (this.isSaveDisabled) {
        return;
      }

      const result = this.sortedRates.map(r => ({
        idInDB: r.idInDB || null,
        counterpartyTypeId: this.isCreate ? null : this.counterpartyTypeId,
        rateVAT: r.value,
        dateOfChange: r.dateFrom,
        isSubjectToVAT: r.value !== 0
      }));

      this.$emit('saveVatRates', result);
      this.$emit('close');
    }
  }
};
</script>
